import {
  ArrowTrendingUpIcon,
  CalendarIcon,
  ChatBubbleOvalLeftIcon,
  EllipsisHorizontalIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { useSyncExternalStore } from 'react';
import { twMerge } from 'tailwind-merge';

import { getBadgeSource, subscribeBadgeSources } from '@/renderer/utils/appBadge';
import { useIsClient } from '@/renderer/utils/hooks/useIsClient';
import { useIsomorphicLayoutEffect } from '@/renderer/utils/hooks/useIsomorphicLayoutEffect';
import { useKeyboardOpen } from '@/renderer/utils/hooks/useKeyboardOpen';
import { isNativePlatform } from '@/renderer/utils/isNativePlatform';
import { NAV_CHECKBOX_ID } from '@/renderer/utils/navDrawer';

/**
 * The mobile primary navigation: a five-tab bar across the bottom of the screen.
 *
 * ## It is the sidebar, not a second navigation
 *
 * This renders from inside renderer/Nav.tsx, AFTER both of Nav's "should this
 * page have navigation at all" gates (HIDE_ON, MARKETING_LANDING_ROUTES). That
 * placement is the point: the tab bar can only appear where the sidebar
 * appears, and can only disappear where the sidebar disappears, so the two can
 * never drift into the state issue #1405 describes — a signed-in user on a
 * guarded route with no way out. Do not lift this to PageLayout or give it
 * route rules of its own.
 *
 * Above `md` it is hidden outright and the desktop sidebar is unchanged. Below
 * `md` the sidebar becomes what the "More" tab opens: everything that is not one
 * of the four destinations here — All Results, Order, settings, account,
 * admin/editor, the sportsbook promo, the guides — still lives in that drawer,
 * which is why More is a plain `<label>` for the SAME `#nav-checkbox` the header
 * hamburger toggles. No second drawer, no second open/close path, and the
 * drawer keeps closing itself on navigation through +onPageTransitionEnd.
 *
 * ## Two treatments, one structure
 *
 * Native (Capacitor) gets the floating translucent capsule; mobile web gets the
 * same five tabs at the same size and placement on a SOLID bar, square to the
 * edges, with a hairline top rule and no `backdrop-filter`. That is a deliberate
 * split, not an oversight: `backdrop-filter` is expensive on mobile browsers,
 * and the glass idiom belongs to the native shell.
 *
 * `isNativePlatform()` reads the global `window.Capacitor` bridge rather than
 * importing `@capacitor/app`, which 500-blanks guarded pages when it is declared
 * but not installed. It is client-only, so the first render (SSR and hydration)
 * is the web treatment on every platform and the capsule swaps in after mount —
 * the same pattern Nav uses for its iOS purchase gating, and the reason there is
 * no hydration mismatch here.
 *
 * ## Layout clearance
 *
 * The bar is `position: fixed`, so nothing reserves room for it on its own. It
 * publishes its full footprint (bar + gap + the bottom safe-area inset) as
 * `--tabbar-height` on <html>, plus a `data-tabbar` marker; renderer/styles/
 * index.css derives `--tabbar-space` from those — 0 above `md` and 0 when no bar
 * is mounted — and pads <main> by it. Surfaces that size themselves to the
 * viewport instead of flowing inside <main> subtract the same variable.
 *
 * ## The keyboard
 *
 * A bottom-anchored bar and the on-screen keyboard want the same strip of
 * screen. The bar yields: while the keyboard is up it translates off the bottom
 * edge and its footprint drops to 0, so it neither sits on top of the keyboard
 * nor fights the release-center composer or the support-chat message box for the
 * space above it, and the layout stops reserving room the bar is not using.
 */

// The bar's own height, and the gap between the floating capsule and the screen
// edge, both from the approved prototype. Kept here (not inline in the class
// strings) because the layout clearance below has to be computed from them.
const NATIVE_BAR_HEIGHT = 66;
const NATIVE_BAR_INSET = 12;
const WEB_BAR_HEIGHT = 62;

/**
 * The key the support-chat unread counter registers under in the shared badge
 * aggregator — WebSocketUnreadCounter keys every source by its `dataPath`, and
 * the sidebar's Support Chat link passes `dataPath: '/support-chat'`. Reading
 * that source is what makes this badge the SAME number the sidebar badge shows
 * (and the same number the OS app-icon badge is summed from) instead of a second
 * count with its own websocket subscription and its own idle/presence handling.
 * See renderer/utils/appBadge.ts.
 */
const CHAT_BADGE_KEY = '/support-chat';

type Tab = {
  href: string;
  /** Prefix match for the active state, when the destination has sub-routes. */
  startsWith?: string;
  icon: typeof MegaphoneIcon;
  label: string;
};

function tabsFor(signedIn: boolean): Tab[] {
  return [
    {
      href: '/release-center',
      icon: MegaphoneIcon,
      label: 'Release',
    },
    // Mirrors the sidebar's single "Results" entry (#2004): members go to their
    // own results, signed-out visitors to the public archive.
    signedIn
      ? { href: '/results', startsWith: '/results', icon: ArrowTrendingUpIcon, label: 'Results' }
      : { href: '/archive', startsWith: '/archive', icon: ArrowTrendingUpIcon, label: 'Results' },
    {
      href: '/release-schedule',
      icon: CalendarIcon,
      label: 'Schedule',
    },
    {
      href: '/support-chat',
      startsWith: '/support-chat',
      icon: ChatBubbleOvalLeftIcon,
      label: 'Chat',
    },
  ];
}

function useChatUnreadCount(): number {
  return useSyncExternalStore(
    subscribeBadgeSources,
    () => getBadgeSource(CHAT_BADGE_KEY),
    () => 0,
  );
}

export function MobileTabBar({
  urlPathname,
  signedIn,
}: { urlPathname: string; signedIn: boolean }) {
  // Client-only: SSR and the first hydration render the web treatment on every
  // platform, then the capsule swaps in. See the doc block above.
  const isClient = useIsClient();
  const native = isClient && isNativePlatform();
  const keyboardOpen = useKeyboardOpen();
  const unread = useChatUnreadCount();

  const footprint = keyboardOpen
    ? '0px'
    : native
      ? `calc(var(--safe-bottom) + ${NATIVE_BAR_INSET * 2 + NATIVE_BAR_HEIGHT}px)`
      : `calc(var(--safe-bottom) + ${WEB_BAR_HEIGHT}px)`;

  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement;

    root.setAttribute('data-tabbar', '');
    root.style.setProperty('--tabbar-height', footprint);

    return () => {
      root.removeAttribute('data-tabbar');
      root.style.removeProperty('--tabbar-height');
    };
  }, [footprint]);

  const tabs = tabsFor(signedIn);

  return (
    <nav
      id="ras-tabbar"
      aria-label="Primary"
      aria-hidden={keyboardOpen || undefined}
      className={twMerge(
        // z-30 (not the sidebar's z-40) is deliberate: the drawer this bar opens
        // has to cover it, and so does the release-center compose sheet and its
        // scrim. It still sits above ordinary page content.
        'fixed z-30 grid grid-cols-5 items-stretch transition-transform duration-200 ease-out md:hidden',
        native
          ? // Floating translucent capsule, inset from all three edges and clear
            // of the home indicator. --safe-bottom, never a bare env(): on
            // Android env(safe-area-inset-bottom) reports only the display
            // cutout, so the bar would sit on the gesture bar (#2092/#2093).
            'right-3 bottom-[calc(12px+var(--safe-bottom))] left-3 h-[66px] rounded-[26px] border border-white/70 bg-white/72 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_8px_28px_rgba(0,0,0,0.13),0_2px_6px_rgba(0,0,0,0.05)] backdrop-blur-[24px] backdrop-saturate-[1.8] dark:border-white/12 dark:bg-zinc-900/72 dark:shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_8px_28px_rgba(0,0,0,0.5)]'
          : // Mobile web: same structure and sizing, solid surface, square to the
            // edges, hairline top rule, and NO backdrop-filter — it is expensive
            // on mobile browsers and the glass belongs to the native shell.
            'inset-x-0 bottom-0 h-[calc(62px+var(--safe-bottom))] border-zinc-900/10 border-t bg-[Canvas] pb-[var(--safe-bottom)] dark:border-white/10',
        // Out of the keyboard's way. Translated fully off the bottom edge rather
        // than unmounted, so the bar animates back to exactly where it was.
        keyboardOpen && 'pointer-events-none translate-y-[calc(100%+var(--safe-bottom)+16px)]',
      )}
    >
      {tabs.map((tab) => (
        <TabLink
          key={tab.href}
          tab={tab}
          active={
            urlPathname === tab.href || (!!tab.startsWith && urlPathname.startsWith(tab.startsWith))
          }
          badge={tab.href === '/support-chat' ? unread : 0}
        />
      ))}

      {/* "More" opens the existing sidebar drawer — the same hidden checkbox the
          header hamburger toggles, so there is exactly one drawer with one
          open/close path (and one that already closes itself on navigation). */}
      <label
        htmlFor={NAV_CHECKBOX_ID}
        aria-label="More"
        className={twMerge(tabClass, 'cursor-pointer')}
      >
        <EllipsisHorizontalIcon className="size-[23px] shrink-0" aria-hidden="true" />
        <span className="font-medium text-[10px] leading-none tracking-tight">More</span>
      </label>
    </nav>
  );
}

// No `hover:` anywhere: Tailwind v4 gates it behind `(hover: hover)`, which
// never matches on the touch devices this bar exists for, so a hover-only
// affordance would simply be unreachable (#1468). Press feedback is `active:`.
// min-h-11 keeps every tap target at 44px; the grid cell is the full bar height
// so the whole column is tappable, not just the icon.
const tabClass =
  'relative flex min-h-11 select-none flex-col items-center justify-center gap-1 text-zinc-500 no-underline transition-opacity active:opacity-60 dark:text-zinc-400';

const activeTabClass = 'text-[#15734A] dark:text-[#34B473]';

function TabLink({ tab, active, badge }: { tab: Tab; active: boolean; badge: number }) {
  const Icon = tab.icon;

  return (
    <a
      href={tab.href}
      data-active={active || undefined}
      aria-current={active ? 'page' : undefined}
      className={twMerge(tabClass, active && activeTabClass)}
    >
      <Icon className="size-[23px] shrink-0" aria-hidden="true" />
      <span
        className={twMerge(
          'font-medium text-[10px] leading-none tracking-tight',
          active && 'font-semibold',
        )}
      >
        {tab.label}
      </span>
      {badge > 0 && (
        <span
          aria-label={`${badge} unread`}
          className="absolute top-1 right-[22px] inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 font-bold text-[10px] text-white leading-none"
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </a>
  );
}

export default MobileTabBar;
